import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Stack, Chip, Button, Divider, Grid,
  IconButton, Collapse, Tooltip, Snackbar,
} from '@mui/material';
import {
  ArrowBack, KeyboardArrowDown, CheckCircle, ContentCopy, Storage,
} from '@mui/icons-material';
import { C } from '../vps/theme';

// Simple inline Ubuntu-style badge icon (orange circle, matches the OS logo
// used in the reference dashboard screenshot).
const UbuntuIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#FFFFFF" />
    <circle cx="12" cy="5.6" r="1.7" fill="#E95420" />
    <circle cx="6.6" cy="15.2" r="1.7" fill="#E95420" />
    <circle cx="17.4" cy="15.2" r="1.7" fill="#E95420" />
    <circle cx="12" cy="12" r="3.3" fill="none" stroke="#E95420" strokeWidth="1.4" />
  </svg>
);

const osIcon = (os = '') => {
  if (os.toLowerCase().includes('ubuntu')) {
    return <UbuntuIcon />;
  }
  return <Storage sx={{ fontSize: 20, color: C.primary }} />;
};

const MOCK_ORDERS = [
  {
    id: 'VPS-2026-8942',
    payId: 'cf_NJ7QXKM9RL',
    date: '28 Jan 2026',
    package: 'NVMe 4 VPS Hosting',
    planLabel: 'NVMe 4',
    specs: '2 vCPU Cores · 4 GB DDR5 RAM · 100GB NVMe SSD',
    hostname: 'my-vps-server',
    serverAddress: 'srv284471.hstsr.cloud',
    os: 'Ubuntu 24.04 LTS',
    ip: '81.112.015.213',
    amount: 396,
    status: 'Active',
    running: true,
    validDays: 365,
  },
  // {
  //   id: 'VPS-2025-4412',
  //   payId: 'cf_MM2TTXBV45',
  //   date: '10 Dec 2025',
  //   package: 'NVMe 2 VPS Hosting',
  //   planLabel: 'NVMe 2',
  //   specs: '1 vCPU Core · 2 GB DDR5 RAM · 50GB NVMe SSD',
  //   hostname: 'dev-sandbox-01',
  //   serverAddress: 'srv611026.hstsr.cloud',
  //   os: 'AlmaLinux 9 Enterprise',
  //   ip: '154.038.221.019',
  //   amount: 198,
  //   status: 'Active',
  //   running: true,
  //   validDays: 128,
  // },
];

const OrdersPage = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(() => Object.fromEntries(MOCK_ORDERS.map((o) => [o.id, true])));
  const [copiedIp, setCopiedIp] = useState('');

  const toggleExpanded = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopyIp = (ip) => {
    navigator.clipboard?.writeText(ip);
    setCopiedIp(ip);
  };

  const stats = [
    { label: 'Total Servers', value: MOCK_ORDERS.length, color: C.primary, bg: C.primaryLight },
    { label: 'Active VPS', value: MOCK_ORDERS.filter(o => o.status === 'Active').length, color: C.success, bg: C.successLight },
  ];

  return (
    <Box sx={{ background: C.bgSection, minHeight: '90vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">

        {/* Back Link */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: C.primary, fontWeight: 600, textTransform: 'none', mb: 2, '&:hover': { background: C.primaryLight } }}
          >
            Back to Home
          </Button>

          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, color: C.textPrimary, mb: 1 }}>
            My VPS Server Orders
          </Typography>
          <Typography sx={{ color: C.textSecondary, fontSize: '0.98rem' }}>
            Track and manage your active VPS hosting deployments and invoices.
          </Typography>
        </Box>

        {/* Stats Row (MUI v6 Grid size) */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {stats.map((s) => (
            <Grid size={{ xs: 6, sm: 3 }} key={s.label}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', background: '#FFFFFF', border: `1px solid ${C.border}` }}>
                <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
                  {s.label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: s.color, mt: 0.5 }}>
                  {s.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Orders List */}
        <Stack spacing={2.5}>
          {MOCK_ORDERS.map((order) => {
            const isOpen = !!expanded[order.id];
            return (
              <Paper elevation={0} key={order.id} sx={{ borderRadius: '16px', background: '#FFFFFF', border: `1px solid ${C.border}`, overflow: 'hidden' }}>

                {/* Collapsed header row — matches the reference dashboard card */}
                <Box
                  onClick={() => toggleExpanded(order.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, p: { xs: 2.5, md: 3 }, cursor: 'pointer', '&:hover': { background: C.bgSection } }}
                >
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <KeyboardArrowDown sx={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease', color: C.primary }} />
                  </IconButton>

                  <Box sx={{ width: 44, height: 44, borderRadius: '10px', background: C.bgSection, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {osIcon(order.os)}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: C.textPrimary, fontSize: '1rem', lineHeight: 1.3 }}>
                      {order.serverAddress}
                    </Typography>
                    <Typography variant="body2" sx={{ color: C.textMuted, fontWeight: 600 }}>
                      {order.planLabel}
                    </Typography>
                  </Box>

                  <Box sx={{ flexGrow: 1 }} />

                  <Chip label={order.status} size="small" sx={{ background: C.successLight, color: C.success, fontWeight: 800, display: { xs: 'none', sm: 'inline-flex' } }} />
                </Box>

                <Collapse in={isOpen}>
                  <Divider sx={{ borderColor: C.border }} />

                  <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ color: C.textMuted, fontWeight: 600 }}>
                          IP address
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary, fontFamily: 'monospace' }}>
                            {order.ip}
                          </Typography>
                          <Tooltip title="Copy IP address">
                            <IconButton size="small" onClick={() => handleCopyIp(order.ip)}>
                              <ContentCopy sx={{ fontSize: 15, color: C.primary }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ color: C.textMuted, fontWeight: 600 }}>
                          Status
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <CheckCircle sx={{ fontSize: 18, color: order.running ? C.success : C.textMuted }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary }}>
                            {order.running ? 'Running' : order.status}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ color: C.textMuted, fontWeight: 600 }}>
                          Valid Upto
                        </Typography>
                        <Chip label={`${order.validDays} Days`} size="small" sx={{ background: C.bgSection, color: C.textPrimary, fontWeight: 700, border: `1px solid ${C.border}` }} />
                      </Stack>
                    </Stack>

                    {/* <Divider sx={{ borderColor: C.border, my: 2.5 }} />

                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 600, display: 'block' }}>
                          Plan
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary }}>
                          {order.package}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 600, display: 'block' }}>
                          Operating System
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary }}>
                          {order.os}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 600, display: 'block' }}>
                          Order ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary }}>
                          {order.id} · {order.date}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 600, display: 'block' }}>
                          Amount Paid
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: C.primary, fontFamily: 'monospace' }}>
                          ₹{order.amount.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.5}>
                      <Button size="small" variant="contained" sx={{ background: C.primary, textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}>
                        Manage VPS
                      </Button>
                      <Button size="small" variant="outlined" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}>
                        View Invoice
                      </Button>
                    </Stack> */}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>

      </Container>

      <Snackbar
        open={!!copiedIp}
        autoHideDuration={2000}
        onClose={() => setCopiedIp('')}
        message={copiedIp ? `Copied ${copiedIp}` : ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default OrdersPage;
